/**
 * The police helicopter, over bigger cities: it circles the city wide and
 * high, and when the police near the player respond (or, later, chase them)
 * it comes down and circles over the player, its searchlight on them by
 * night.
 *
 * It flies by plain steering: toward the point on its circle a little ahead
 * of where it is, no faster than a police helicopter cruises, its nose the
 * way it goes, tipped forward with its speed and banked into its turn. It
 * keeps its place as a geographic anchor and an offset from it, so the
 * scene's origin moving does not move it.
 */

import type { Scene } from 'three';
import { Vector3 } from 'three';
import type { HelicopterModel } from './Helicopter.js';
import { HELI, Searchlight, animateHelicopterLights, createHelicopter, orientHelicopter, repaintHelicopter } from './Helicopter.js';

/** Patrolling: how high over the ground, metres, how wide a circle, and how fast, m/s. */
const PATROL = { height: 300, radius: 500, speed: 32 };
/** Over the player: lower, tighter, and a little slower. */
const WATCH = { height: 150, radius: 110, speed: 26 };
/** The fastest it flies, m/s (about 240 km/h), and how quickly it takes up speed, m/s². */
const TOP_SPEED = 66;
const ACCEL = 6;
/** Further than this from its anchor, metres, the player has left the city: it stays behind. */
const LEAVE = 3500;

export interface PoliceHelicopterWorld {
  /** Scene x, z of a place. */
  toLocal: (lat: number, lon: number) => { east: number; north: number };
  /** The place at scene (x, z). */
  toGeo: (x: number, z: number) => { lat: number; lon: number };
  /** The ground under scene (x, z), if streamed. */
  ground: (x: number, z: number) => number | undefined;
}

const toward = new Vector3();
const down = new Vector3();

export class PoliceHelicopter {
  readonly model: HelicopterModel;
  private readonly searchlight: Searchlight;
  /** Where it circles round, as a place, once over a city. */
  private anchor: { lat: number; lon: number } | undefined;
  /** Where it is, scene metres from the anchor, how high, and how fast it goes (scene x, z). */
  private x = 0;
  private z = 0;
  private y = 0;
  private vx = 0;
  private vz = 0;
  private yaw = 0;
  private roll = 0;
  private clock = 0;
  /** Which way round it circles: anticlockwise from above. */
  private angle = 0;

  constructor(scene: Scene, colours: { flash: number; bands: number }) {
    this.model = createHelicopter();
    repaintHelicopter(this.model, colours.flash, colours.bands);
    this.model.root.visible = false;
    scene.add(this.model.root);
    this.searchlight = new Searchlight(scene);
  }

  /** Repaint it for the country it flies over. */
  repaint(colours: { flash: number; bands: number }): void {
    repaintHelicopter(this.model, colours.flash, colours.bands);
  }

  /** Whether it is about. */
  get flying(): boolean {
    return this.model.root.visible;
  }

  /**
   * On by `dt`. `city`: whether the player is in a city big enough for one;
   * `watch`: scene (x, z) to circle low over, or undefined to patrol;
   * `player`: scene (x, z) of the player; `night` 0..1.
   */
  update(dt: number, world: PoliceHelicopterWorld, city: boolean, player: { x: number; z: number }, watch: { x: number; z: number } | undefined, night: number): void {
    this.clock += dt;
    if (this.anchor !== undefined) {
      const a = world.toLocal(this.anchor.lat, this.anchor.lon);
      if (Math.hypot(player.x - a.east, player.z + a.north) > LEAVE) this.anchor = undefined;
    }
    if (this.anchor === undefined) {
      if (!city) {
        this.model.root.visible = false;
        this.searchlight.update(undefined, down, night);
        return;
      }
      // Over a new city: it comes in from the edge of its circle, high.
      this.anchor = world.toGeo(player.x, player.z);
      this.angle = Math.random() * Math.PI * 2;
      this.x = Math.cos(this.angle) * PATROL.radius;
      this.z = Math.sin(this.angle) * PATROL.radius;
      this.y = (world.ground(player.x, player.z) ?? 0) + PATROL.height;
      this.vx = this.vz = 0;
    }
    const a = world.toLocal(this.anchor.lat, this.anchor.lon);
    const ax = a.east, az = -a.north;
    const job = watch === undefined ? PATROL : WATCH;
    // The circle's middle, from the anchor: the city, or the one it watches.
    const cx = watch === undefined ? 0 : watch.x - ax, cz = watch === undefined ? 0 : watch.z - az;
    // A point on the circle a little ahead of the nearest: round it goes.
    const rx = this.x - cx, rz = this.z - cz;
    const here = Math.atan2(rz, rx);
    const ahead = here + Math.min(0.6, (job.speed * 2.5) / job.radius);
    const tx = cx + Math.cos(ahead) * job.radius, tz = cz + Math.sin(ahead) * job.radius;
    const dx = tx - this.x, dz = tz - this.z, d = Math.hypot(dx, dz) || 1;
    // Far off the circle, it hurries to it; on it, cruises round.
    const speed = Math.min(TOP_SPEED, job.speed + Math.max(0, Math.hypot(rx, rz) - job.radius) * 0.15);
    const wantX = (dx / d) * speed, wantZ = (dz / d) * speed;
    const dvx = wantX - this.vx, dvz = wantZ - this.vz, dv = Math.hypot(dvx, dvz) || 1;
    const step = Math.min(dv, ACCEL * dt);
    this.vx += (dvx / dv) * step;
    this.vz += (dvz / dv) * step;
    this.x += this.vx * dt;
    this.z += this.vz * dt;
    const sx = ax + this.x, sz = az + this.z;
    const floor = (world.ground(sx, sz) ?? this.y - job.height) + job.height;
    this.y += Math.max(-5 * dt, Math.min(5 * dt, floor - this.y));
    // Nose the way it goes, tipped forward with speed, banked into the turn.
    const v = Math.hypot(this.vx, this.vz);
    const was = this.yaw;
    if (v > 2) this.yaw = Math.atan2(-this.vx, -this.vz);
    const turn = dt > 0 ? Math.atan2(Math.sin(this.yaw - was), Math.cos(this.yaw - was)) / dt : 0;
    this.roll += (Math.max(-0.4, Math.min(0.4, Math.atan((v * turn) / 9.81))) - this.roll) * Math.min(1, dt * 2);
    const root = this.model.root;
    root.visible = true;
    root.position.set(sx, this.y - HELI.skidHeight, sz);
    orientHelicopter(root, this.yaw, -0.12 * (v / TOP_SPEED) * 2, this.roll);
    this.model.mainRotor.rotation.y += HELI.rotorSpeed * dt;
    this.model.tailRotor.rotation.x += HELI.rotorSpeed * 1.8 * dt;
    animateHelicopterLights(this.model, true, night, this.clock);
    // By night, its searchlight: on the one it watches, else down ahead of it.
    root.updateMatrixWorld(true);
    if (watch !== undefined) {
      toward.set(watch.x, world.ground(watch.x, watch.z) ?? this.y - job.height, watch.z).sub(root.position).normalize();
    } else {
      toward.set(-Math.sin(this.yaw) * 0.5, -1, -Math.cos(this.yaw) * 0.5).normalize();
    }
    this.searchlight.update(this.model, toward, night);
  }

  /** Where it is in the scene, if flying. */
  get position(): Vector3 | undefined {
    return this.model.root.visible ? this.model.root.position : undefined;
  }
}
